import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { Bar, Radar } from 'react-chartjs-2';
import type { MatchResult } from '@crossmatch/shared';
import { clamp01, formatScore, schoolLabel } from '../lib/format.js';

// Chart.js v4 需要显式注册（本地依赖，不走 CDN，符合 CSP 'self'）
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

// 评审硬指标：图表内的文字也不小于 13px
const TICK_FONT = { size: 14 };
const TICK_COLOR = '#556579';
const GRID_COLOR = 'rgba(15, 23, 42, 0.10)';

export interface ScoreBarChartProps {
  results: MatchResult[];
  max?: number;
}

/** 候选人排序分对比（数据完全来自后端 MatchResult.total，前端不做换算）。 */
export function ScoreBarChart({ results, max = 8 }: ScoreBarChartProps) {
  const top = results.slice(0, max);
  const data: ChartData<'bar'> = {
    labels: top.map((item) => `${item.name}（${schoolLabel(item.school)}）`),
    datasets: [
      {
        label: '排序分（0–100）',
        data: top.map((item) => item.total),
        backgroundColor: 'rgba(14, 116, 144, 0.25)',
        borderColor: '#0e7490',
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        titleFont: TICK_FONT,
        bodyFont: TICK_FONT,
        callbacks: {
          label: (item) => `排序分 ${formatScore(item.parsed.y)}（不是准确率/概率）`,
        },
      },
    },
    scales: {
      x: { ticks: { color: TICK_COLOR, font: TICK_FONT }, grid: { color: GRID_COLOR } },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { color: TICK_COLOR, font: TICK_FONT },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return (
    <div className="chart-wrap">
      <Bar data={data} options={options} aria-label="候选人排序分对比图" />
    </div>
  );
}

export interface ComponentRadarChartProps {
  result: MatchResult;
}

/** 单个候选人的四项分量结构（0–1 → 0–100 仅用于画图，不代表任何新算法）。 */
export function ComponentRadarChart({ result }: ComponentRadarChartProps) {
  const { breakdown } = result;
  const data: ChartData<'radar'> = {
    labels: ['需求覆盖', '团队互补', '时间协同', '跨校'],
    datasets: [
      {
        label: `${result.name} 的四项分量`,
        data: [breakdown.coverage, breakdown.complement, breakdown.time, breakdown.crossSchool].map((value) =>
          Math.round(clamp01(value) * 100),
        ),
        backgroundColor: 'rgba(14, 116, 144, 0.14)',
        borderColor: '#0e7490',
        borderWidth: 2,
        pointBackgroundColor: '#0e7490',
        pointRadius: 3,
      },
    ],
  };
  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        titleFont: TICK_FONT,
        bodyFont: TICK_FONT,
        callbacks: {
          label: (item) => `${item.label} ${formatScore(item.parsed.r)}%（后端分量值）`,
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        angleLines: { color: GRID_COLOR },
        grid: { color: GRID_COLOR },
        pointLabels: { color: '#334155', font: TICK_FONT },
        ticks: { color: TICK_COLOR, backdropColor: 'transparent', font: TICK_FONT, stepSize: 25 },
      },
    },
  };

  return (
    <div className="chart-wrap">
      <Radar data={data} options={options} aria-label={`${result.name} 的四项分量雷达图`} />
    </div>
  );
}